import { useState, type SyntheticEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { formatUsd } from '../../lib/format';
import { useAssetComponents } from '../../lib/assetComponents';

/**
 * Probe asset grouping (spec section 4): the list is top-level by default, so
 * a server row carries "+N components", and a search hit on a component says
 * which server it is in. Rendered under the Model cell because DataTable has
 * no sub-rows. The row navigates on click and Enter/Space, so every control
 * here stops propagation.
 */
const stop = (e: SyntheticEvent) => e.stopPropagation();

export function ComponentLinks({
  assetId,
  componentCount,
  parentAssetId,
  parentLabel,
  parentIsUnidentifiedChassis,
}: {
  assetId: string;
  componentCount: number;
  parentAssetId: string | null;
  parentLabel: string | null;
  /**
   * SIL-1202: the parent is still an unidentified, Customs-held chassis.
   * Rendered as plain text, not a Link — the customer cannot triage or view
   * that chassis, so a link would only ever 404 or (for an org admin, who
   * bypasses team-scoped RLS) surface the ops Customs surface out of context.
   */
  parentIsUnidentifiedChassis?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {parentAssetId && (
        <div className="text-xs text-zinc-500">
          in{' '}
          {parentIsUnidentifiedChassis ? (
            <span className="font-medium text-zinc-700">{parentLabel ?? 'Unidentified chassis'}</span>
          ) : (
            <Link
              to="/assets/$assetId"
              params={{ assetId: parentAssetId }}
              onClick={stop}
              onKeyDown={stop}
              className="font-medium text-brand-600 hover:underline"
            >
              {parentLabel ?? parentAssetId.slice(0, 8)}
            </Link>
          )}
        </div>
      )}
      {componentCount > 0 && (
        <>
          <button
            type="button"
            aria-expanded={open}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
            onKeyDown={stop}
            className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:underline"
          >
            <ChevronRight className={cn('size-3 transition-transform', open && 'rotate-90')} aria-hidden />
            +{componentCount} {componentCount === 1 ? 'component' : 'components'}
          </button>
          {open && <ComponentList assetId={assetId} />}
        </>
      )}
    </>
  );
}

function ComponentList({ assetId }: { assetId: string }) {
  const { data, isLoading, isError } = useAssetComponents(assetId);
  if (isLoading) return <p className="mt-1 text-xs text-zinc-500">Loading components...</p>;
  if (isError || !data) return <p className="mt-1 text-xs text-amber-700">Could not load components.</p>;

  return (
    <ul className="mt-1 space-y-0.5 border-l border-zinc-200 pl-2">
      {data.components.map((c) => (
        <li key={c.assetId} className="flex flex-wrap items-center gap-2 text-xs">
          <Link
            to="/assets/$assetId"
            params={{ assetId: c.assetId }}
            onClick={stop}
            onKeyDown={stop}
            className="text-zinc-700 hover:text-brand-700 hover:underline"
          >
            {c.modelName ?? 'Accelerator'} - GPU {c.deviceIndex ?? '-'}
          </Link>
          <span className="tabular-nums text-zinc-500">{c.fmv ? formatUsd(c.fmv.valueUsd) : 'Not priced'}</span>
          {c.isMissing && <span className="text-amber-700">not seen recently</span>}
        </li>
      ))}
    </ul>
  );
}
